import apiClient from './api';

export interface ReservationInUse {
  id: number;
  startTime: string;
  durationMinutes: number;
  headCount: number;
  facility: {
    id: number;
    name: string;
    description: string;
    purpose: string;
    equipment: string;
    caution: string;
    location: string;
    imageUrl: string;
  };
  member: {
    name: string;
    profileImage: string;
    universityId: number;
  };
}

export interface BookedTimeSlot {
  time: string;
}

export interface CreateReservationRequest {
  facilityId: number;
  headCount: number;
  startTime: string;
  durationMinutes: number;
  alarmTimings: string[];
}

export interface CreateReservationResponse {
  id: number;
  member: {
    name: string;
    studentId: string;
    profileImage: string | null;
    department: string;
  };
  facility: {
    id: number;
    name: string;
    description: string | null;
    purpose: string | null;
    equipment: string | null;
    caution: string | null;
    location: string | null;
    imageUrl: string | null;
    images: string[];
  };
  startTime: string;
  endTime: string;
}

export interface CreateReservationData {
  memberId: number;
  facilityId: number;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  alerts?: string[];
}

export interface ExtendReservationData {
  reservation_id: number;
  extendTime: string;
}

export interface ExtendReservationRequest {
  endTime: string;
}

export interface ReturnReservationData {
  images: File[];
  checklist: any;
}

export interface ReservationDetail {
  id: number;
  facilityName: string;
  date: string;
  time: string;
  status: string;
}

export interface ReservationByFacility {
  date: string;
  times: string[];
}

export interface AlarmOption {
  name: string;
  displayName: string;
  minutes: number;
}

export interface MyReservation {
  id: number;
  startTime: string;
  durationMinutes: number;
  headCount: number;
  facility: {
    id: number;
    name: string;
    description: string;
    purpose: string;
    equipment: string;
    caution: string;
    location: string;
    imageUrl: string;
    building?: {
      id: number;
      name: string;
      latitude: number;
      longitude: number;
    };
  };
  member: {
    name: string;
    profileImage: string | null;
    universityId: number;
  };
}

export const reservationService = {
  getInUseReservation: async (): Promise<ReservationInUse | null> => {
    try {
      const response = await apiClient.get<ReservationInUse>('/me/reservations/in-use');
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.code === 'COMMON403' || error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  getBookedTimeSlots: async (
    facilityId: number,
    year: number,
    month: number,
    day: number
  ): Promise<BookedTimeSlot[]> => {
    const response = await apiClient.get<BookedTimeSlot[]>('/reservation/time', {
      params: { facilityId, year, month, day },
    });
    return response.data;
  },

  createReservation: async (data: CreateReservationData): Promise<void> => {
    const response = await apiClient.post('/reservation', data);
    return response.data;
  },

  extendReservation: async (data: ExtendReservationData): Promise<void> => {
    const response = await apiClient.post('/reservation/extend', data);
    return response.data;
  },

  returnReservation: async (data: ReturnReservationData): Promise<void> => {
    const formData = new FormData();
    data.images.forEach((image) => {
      formData.append('images', image);
    });
    formData.append('checklist', JSON.stringify(data.checklist));

    const response = await apiClient.post('/reservation/return', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getReservationDetails: async (memberId: number, page: number = 1): Promise<any> => {
    const response = await apiClient.get('/reservation/details', {
      params: { memberId, page },
    });
    return response.data;
  },

  getReservationsByFacility: async (
    facilityId: number,
    page: number = 1
  ): Promise<ReservationByFacility[]> => {
    const response = await apiClient.get('/reservation/byfacility', {
      params: { facilityId, page },
    });
    return response.data;
  },

  getAlarmOptions: async (): Promise<AlarmOption[]> => {
    const response = await apiClient.get<AlarmOption[]>('/reservations/alert-options');
    return response.data;
  },

  createNewReservation: async (data: CreateReservationRequest): Promise<CreateReservationResponse> => {
    const response = await apiClient.post<CreateReservationResponse>('/reservations', data);
    return response.data;
  },

  getMyReservations: async (): Promise<MyReservation[]> => {
    const response = await apiClient.get<MyReservation[]>('/me/reservations');
    return response.data;
  },

  getReservationById: async (reservationId: number): Promise<MyReservation> => {
    const response = await apiClient.get<MyReservation>(`/reservations/${reservationId}`);
    return response.data;
  },

  getPendingReviewReservations: async (): Promise<MyReservation[]> => {
    const response = await apiClient.get<MyReservation[]>('/me/reservations', {
      params: { onlyPendingReview: true }
    });
    return response.data;
  },

  extendReservationById: async (reservationId: number, data: ExtendReservationRequest): Promise<void> => {
    const response = await apiClient.patch(`/reservations/${reservationId}/extend`, data);
    return response.data;
  },

  uploadReservationImages: async (images: File[]): Promise<string[]> => {
    const uploadPromises = images.map(async (image) => {
      const formData = new FormData();
      formData.append('file', image);

      const response = await apiClient.post<{url: string}>('/reservations/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    });

    const urls = await Promise.all(uploadPromises);
    return urls;
  },

  returnReservationById: async (reservationId: number, imageUrls: string[]): Promise<MyReservation> => {
    const response = await apiClient.patch<MyReservation>(`/reservations/${reservationId}/return`, {
      images: imageUrls,
    });
    return response.data;
  },
};
